import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup, StatusToggle } from '../components/ActionControls';
import { usePackage } from '../store/slice/packageSlice';
import { useSalon } from '../store/slice/salonSlice';

const initialForm = {
  name: '',
  description: '',
  price: '',
  duration_minutes: '',
  salon_id: '',
  is_active: true,
};

function toLabel(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function parseStatus(item) {
  const statusValue = item?.status ?? item?.is_active ?? item?.isActive ?? item?.active;
  if (typeof statusValue === 'string') return statusValue.toLowerCase() !== 'inactive';
  return Boolean(statusValue);
}

function parseName(item) {
  return item?.name || item?.package_name || item?.title || item?.packageName || 'Untitled';
}

function parseDescription(item) {
  return item?.description || item?.details || item?.package_description || '';
}

function parseSalon(item) {
  return (
    item?.salon_name ||
    item?.salonName ||
    item?.salon?.name ||
    item?.salon_id ||
    item?.salonId ||
    ''
  );
}

function parsePrice(item) {
  return item?.price ?? item?.amount ?? item?.total_price ?? item?.package_price ?? '';
}

function parseDuration(item) {
  return item?.duration ?? item?.duration_minutes ?? item?.durationInMinutes ?? '';
}

function parseSalonId(item) {
  return item?.salon_id ?? item?.salonId ?? item?.salon?.id ?? '';
}

function formatCurrency(value) {
  if (value === '' || value === null || value === undefined) return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';
  return `$${amount.toFixed(2)}`;
}

function PackagesPage() {
  const {
    packages,
    loading,
    error,
    fetchPackages,
    createPackage,
    updatePackage,
    deletePackage,
    togglePackageActive,
  } = usePackage();
  const { salons, fetchSalons } = useSalon();

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    sortBy: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    fetchPackages();
    fetchSalons();
  }, [fetchPackages, fetchSalons]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      value: filters.status,
      defaultValue: 'all',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'name-asc', label: 'Name (A-Z)' },
        { value: 'name-desc', label: 'Name (Z-A)' },
        { value: 'price-asc', label: 'Price (Low-High)' },
        { value: 'price-desc', label: 'Price (High-Low)' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status !== 'all') count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredPackages = useMemo(() => {
    let result = [...packages];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter((pkg) => {
        const matchedSalon = salons.find((salon) => String(salon.id) === String(parseSalonId(pkg)));
        const blob = `${parseName(pkg)} ${parseDescription(pkg)} ${parseSalon(pkg)} ${matchedSalon?.name || ''}`.toLowerCase();
        return blob.includes(term);
      });
    }
    if (filters.status !== 'all') {
      const needsActive = filters.status === 'active';
      result = result.filter((pkg) => parseStatus(pkg) === needsActive);
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va;
        let vb;
        if (field === 'price') {
          va = Number(parsePrice(a) || 0);
          vb = Number(parsePrice(b) || 0);
        } else {
          va = parseName(a).toLowerCase();
          vb = parseName(b).toLowerCase();
        }
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [packages, filters, salons]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setIsModalOpen(true);
  };

  const openEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: parseName(pkg),
      description: parseDescription(pkg),
      price: toLabel(parsePrice(pkg)) === '—' ? '' : String(parsePrice(pkg)),
      duration_minutes: toLabel(parseDuration(pkg)) === '—' ? '' : String(parseDuration(pkg)),
      salon_id: toLabel(parseSalonId(pkg)) === '—' ? '' : String(parseSalonId(pkg)),
      is_active: parseStatus(pkg),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Package name is required');
      return;
    }

    const price = form.price === '' ? null : Number(form.price);
    if (price !== null && (Number.isNaN(price) || price < 0)) {
      toast.error('Price must be 0 or greater');
      return;
    }

    const durationMinutes =
      form.duration_minutes === '' ? null : parseInt(form.duration_minutes, 10);
    if (durationMinutes !== null && (Number.isNaN(durationMinutes) || durationMinutes <= 0)) {
      toast.error('Duration must be greater than 0');
      return;
    }

    const salonId = form.salon_id === '' ? null : parseInt(form.salon_id, 10);
    if (salonId === null) {
      toast.error('Salon is required');
      return;
    }
    if (form.salon_id !== '' && Number.isNaN(salonId)) {
      toast.error('Please select a valid salon');
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      is_active: form.is_active ? 1 : 0,
    };
    if (price !== null) payload.price = price;
    if (durationMinutes !== null) payload.duration_minutes = durationMinutes;
    if (salonId !== null) payload.salon_id = salonId;

    const action = editing
      ? updatePackage({ id: editing.id, payload })
      : createPackage(payload);

    const result = await action;
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(editing ? 'Package updated' : 'Package created');
      setIsModalOpen(false);
      setEditing(null);
      setForm(initialForm);
      fetchPackages();
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Delete package "${parseName(pkg)}"?`)) return;
    const result = await deletePackage(pkg.id);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success('Package deleted');
      fetchPackages();
    }
  };

  const handleToggle = async (pkg) => {
    const result = await togglePackageActive(pkg.id);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(`Package ${parseStatus(pkg) ? 'deactivated' : 'activated'}`);
      fetchPackages();
    }
  };

  const getSalonLabel = (pkg) => {
    const salonId = parseSalonId(pkg);
    const matchedSalon = salons.find((salon) => String(salon.id) === String(salonId));
    return matchedSalon?.name || toLabel(parseSalon(pkg));
  };

  const formatDuration = (pkg) => {
    const duration = parseDuration(pkg);
    return duration === '' || duration === null || duration === undefined ? '—' : `${duration} mins`;
  };

  if (loading && packages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading packages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Packages</h1>
          <p className="mt-1 text-slate-500">Create and manage salon packages</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center gap-2">
          <span>+</span> Add Package
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Packages</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{packages.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredPackages.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Active</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {packages.filter((pkg) => parseStatus(pkg)).length}
          </p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search package by name, salon, description..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Salon</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Price</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPackages.map((pkg) => {
                const active = parseStatus(pkg);
                return (
                  <tr key={pkg.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-6 font-medium text-slate-800">{parseName(pkg)}</td>
                    <td className="py-4 px-6 text-slate-600">{getSalonLabel(pkg)}</td>
                    <td className="py-4 px-6 text-slate-600">{formatCurrency(parsePrice(pkg))}</td>
                    <td className="py-4 px-6 text-slate-600">{formatDuration(pkg)}</td>
                    <td className="py-4 px-6">
                      <span
                        className={`status-pill ${
                          active ? 'status-pill-success' : 'status-pill-muted'
                        }`}
                      >
                        {active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-slate-600 max-w-xs truncate" title={parseDescription(pkg)}>
                      {parseDescription(pkg) || '—'}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <ActionButtonGroup>
                        <ActionButton
                          type="button"
                          variant="edit"
                          onClick={() => openEdit(pkg)}
                          disabled={loading}
                        >
                          Edit
                        </ActionButton>
                        <StatusToggle
                          checked={active}
                          onChange={() => handleToggle(pkg)}
                          disabled={loading}
                          label={`Toggle package status for ${parseName(pkg)}`}
                          showStateLabel={false}
                        />
                        <ActionButton
                          type="button"
                          variant="danger"
                          onClick={() => handleDelete(pkg)}
                          disabled={loading}
                        >
                          Delete
                        </ActionButton>
                      </ActionButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredPackages.length === 0 && (
          <div className="py-16 text-center text-slate-500">No packages found.</div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-premium w-full max-w-lg shadow-premium-xl">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <h2 className="text-xl font-semibold text-slate-800">
                {editing ? 'Edit Package' : 'Create Package'}
              </h2>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Package Name *</label>
                <input
                  className="input-premium w-full"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Premium Hair Care"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Description</label>
                <textarea
                  className="input-premium w-full min-h-[90px]"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="What's included in this package?"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Price</label>
                  <input
                    type="number"
                    min="0"
                    className="input-premium w-full"
                    value={form.price}
                    onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                    placeholder="499"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Duration (mins)</label>
                  <input
                    type="number"
                    min="1"
                    className="input-premium w-full"
                    value={form.duration_minutes}
                    onChange={(e) => setForm((prev) => ({ ...prev, duration_minutes: e.target.value }))}
                    placeholder="90"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Salon *</label>
                <select
                  className="input-premium w-full"
                  value={form.salon_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, salon_id: e.target.value }))}
                  required
                >
                  <option value="">Select salon</option>
                  {salons.map((salon) => (
                    <option key={salon.id} value={salon.id}>
                      {salon.name || `Salon #${salon.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="package_is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="package_is_active" className="text-sm font-medium text-slate-600">
                  Active
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-premium border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
                  {loading ? (editing ? 'Saving...' : 'Creating...') : (editing ? 'Save Changes' : 'Create Package')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PackagesPage;
